import { getItem } from './cookie'
import { TOKEN } from '../constant'

const LAST_ACTIVITY_KEY = 'transport:lastUserActivity'
const HEARTBEAT_INTERVAL_MS = 30 * 1000
const CHECK_INTERVAL_MS = 5 * 1000
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'click', 'touchstart', 'wheel', 'scroll']

let idleMinutes = 30
let lastHeartbeatAt = 0
let lastRecordedAt = 0
let fallbackActivityAt = null
let timer = null
let trailingHeartbeatTimer = null
let heartbeatPending = false
let onTimeout = null

function storedActivityAt () {
  try {
    const value = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY))
    return Number.isFinite(value) && value > 0 ? value : fallbackActivityAt
  } catch (error) {
    return fallbackActivityAt
  }
}

function saveActivityAt (value) {
  fallbackActivityAt = value
  try { window.localStorage.setItem(LAST_ACTIVITY_KEY, String(value)) } catch (error) { /* private browsing fallback */ }
}

function clearActivityAt () {
  fallbackActivityAt = null
  try { window.localStorage.removeItem(LAST_ACTIVITY_KEY) } catch (error) { /* private browsing fallback */ }
}

export function setIdleMinutes (value) {
  const minutes = Number(value)
  idleMinutes = Number.isInteger(minutes) && minutes >= 1 && minutes <= 120 ? minutes : 30
  checkIdleTimeout()
}

function expire (reason = 'idle') {
  if (!timer) return
  const callback = onTimeout
  stopIdleSession()
  try {
    window.sessionStorage.setItem(reason === 'disabled' ? 'accountDisabledNotice' : 'idleLogoutNotice', '1')
  } catch (error) { /* optional notice */ }
  if (callback) callback()
}

function checkIdleTimeout () {
  if (!timer) return
  if (!getItem(TOKEN)) {
    stopIdleSession()
    return
  }
  const lastActivityAt = storedActivityAt()
  if (lastActivityAt && Date.now() - lastActivityAt >= idleMinutes * 60 * 1000) expire()
}

async function heartbeat () {
  if (heartbeatPending || !getItem(TOKEN)) return
  const token = getItem(TOKEN)
  heartbeatPending = true
  lastHeartbeatAt = Date.now()
  try {
    const response = await window.fetch('/api/admin/user/getLoginUser', {
      method: 'GET',
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}`, 'X-User-Activity': 'true' }
    })
    const result = await response.json()
    if (!timer || getItem(TOKEN) !== token) return
    if (result.code === 'A0202') {
      expire('disabled')
    } else if (result.code === 'A0311' || result.code === 'A0230' || response.status === 401) {
      expire()
    } else if (result.code === '00000' && result.data) {
      setIdleMinutes(result.data.idleLogoutMinutes)
    }
  } catch (error) {
    // A temporary network failure does not change the local inactivity clock.
  } finally {
    heartbeatPending = false
  }
}

function recordActivity () {
  if (!timer || document.hidden) return
  const now = Date.now()
  if (now - lastRecordedAt < 1000) return
  lastRecordedAt = now
  checkIdleTimeout()
  if (!timer) return
  saveActivityAt(now)
  if (now - lastHeartbeatAt >= HEARTBEAT_INTERVAL_MS) heartbeat()
  if (trailingHeartbeatTimer) window.clearTimeout(trailingHeartbeatTimer)
  trailingHeartbeatTimer = window.setTimeout(() => {
    trailingHeartbeatTimer = null
    checkIdleTimeout()
    if (timer && Date.now() - lastHeartbeatAt >= HEARTBEAT_INTERVAL_MS) heartbeat()
  }, HEARTBEAT_INTERVAL_MS)
}

function onStorageChange (event) {
  if (event.key === LAST_ACTIVITY_KEY) {
    if (event.newValue == null) expire()
    else checkIdleTimeout()
  }
}

function onVisibilityChange () { if (!document.hidden) checkIdleTimeout() }

export function markLoginActivity () { saveActivityAt(Date.now()) }

export function startIdleSession (minutes, timeoutCallback) {
  stopIdleSession(false)
  onTimeout = timeoutCallback
  setIdleMinutes(minutes)
  if (!storedActivityAt()) markLoginActivity()
  ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, recordActivity, { passive: true }))
  window.addEventListener('storage', onStorageChange)
  document.addEventListener('visibilitychange', onVisibilityChange)
  timer = window.setInterval(checkIdleTimeout, CHECK_INTERVAL_MS)
  checkIdleTimeout()
}

export function stopIdleSession (clearActivity = true) {
  if (timer) window.clearInterval(timer)
  if (trailingHeartbeatTimer) window.clearTimeout(trailingHeartbeatTimer)
  timer = null
  trailingHeartbeatTimer = null
  ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, recordActivity))
  window.removeEventListener('storage', onStorageChange)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  onTimeout = null
  heartbeatPending = false
  lastHeartbeatAt = 0
  lastRecordedAt = 0
  if (clearActivity) clearActivityAt()
}
